/**
 * Trip Details+ modal — flight tracking, extras (gratuity/tolls/wait), farm-out tracking.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { X, Plane, DollarSign, ArrowRightLeft, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentsSection } from '../FileAttachments';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;
const EXTRA_TYPES = [
  { v: 'gratuity', l: 'Gratuity' }, { v: 'tolls', l: 'Tolls' }, { v: 'wait_time', l: 'Wait Time' },
  { v: 'cleaning', l: 'Cleaning Fee' }, { v: 'fuel', l: 'Fuel Surcharge' }, { v: 'damage', l: 'Damage' }, { v: 'other', l: 'Other' },
];

export default function TripDetailsModal({ booking, onClose, onSaved }) {
  const [b, setB] = useState(booking);
  const [flight, setFlight] = useState(booking.flight_number || '');
  const [flightInfo, setFlightInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [extraForm, setExtraForm] = useState({ type: 'gratuity', amount: '', description: '' });
  const [farmout, setFarmout] = useState({
    is_farmout: !!booking.is_farmout,
    farmout_partner: booking.farmout_partner || '',
    farmout_cost: booking.farmout_cost || '',
    farmout_confirmation: booking.farmout_confirmation || '',
  });

  const put = async (payload) => {
    const r = await fetch(`${API}/api/command-center/confirmed-bookings/${b.booking_id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error('Save failed');
  };

  const saveFlight = async () => {
    try { await put({ flight_number: flight.trim().toUpperCase() }); setB(p => ({ ...p, flight_number: flight.trim().toUpperCase() })); toast.success('Flight saved — shows on driver trip sheet'); onSaved(); }
    catch (e) { toast.error(e.message); }
  };

  const checkFlight = async () => {
    if (!flight.trim()) { toast.error('Enter a flight number'); return; }
    setChecking(true);
    try {
      const r = await fetch(`${API}/api/ops/flight-status?flight=${encodeURIComponent(flight.trim())}`);
      setFlightInfo(await r.json());
    } catch { toast.error('Lookup failed'); }
    setChecking(false);
  };

  const addExtra = async () => {
    const amount = Number(extraForm.amount);
    if (!amount) { toast.error('Amount required'); return; }
    const r = await fetch(`${API}/api/ops/bookings/${b.booking_id}/extras`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...extraForm, amount }),
    });
    const d = await r.json();
    if (r.ok) { setB(d.booking); setExtraForm({ type: 'gratuity', amount: '', description: '' }); toast.success('Charge added to booking + client ledger'); onSaved(); }
    else toast.error(d.detail || 'Failed');
  };

  const removeExtra = async (extraId) => {
    const r = await fetch(`${API}/api/ops/bookings/${b.booking_id}/extras/${extraId}`, { method: 'DELETE' });
    const d = await r.json();
    if (r.ok) { setB(d.booking); toast.success('Charge removed'); onSaved(); }
  };

  const saveFarmout = async () => {
    try {
      const payload = { ...farmout, farmout_cost: Number(farmout.farmout_cost) || 0 };
      await put(payload);
      setB(p => ({ ...p, ...payload }));
      toast.success(payload.is_farmout ? 'Farm-out saved' : 'Farm-out cleared');
      onSaved();
    } catch (e) { toast.error(e.message); }
  };

  const margin = (Number(b.total) || 0) - (Number(farmout.farmout_cost) || 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <Card className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="trip-details-modal">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center rounded-t-2xl z-10">
          <div>
            <h3 className="font-bold text-sm">Trip Details+ · {b.client_name}</h3>
            <p className="text-[10px] text-gray-400">{b.booking_id} · {b.event_date} · Total {f(b.total)}</p>
          </div>
          <button onClick={onClose} data-testid="trip-details-close"><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* Flight */}
          <section data-testid="flight-section">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1"><Plane className="h-3 w-3" /> Airport Run — Flight Tracking</p>
            <div className="flex gap-2">
              <Input value={flight} onChange={e => setFlight(e.target.value)} placeholder="e.g. DL1234" className="h-9 text-xs uppercase" data-testid="flight-input" />
              <Button size="sm" variant="outline" onClick={saveFlight} className="h-9 text-xs" data-testid="flight-save">Save</Button>
              <Button size="sm" onClick={checkFlight} disabled={checking} className="bg-black hover:bg-gray-800 h-9 text-xs" data-testid="flight-check">{checking ? '…' : 'Check Status'}</Button>
            </div>
            {flightInfo && (
              <div className="mt-2 border border-gray-200 p-3 text-xs" data-testid="flight-result">
                {flightInfo.live ? (
                  <>
                    <p className="font-bold uppercase">{flightInfo.airline} {flightInfo.flight} — <span className={flightInfo.status === 'active' ? 'text-green-700' : flightInfo.status === 'cancelled' ? 'text-red-600' : 'text-amber-700'}>{flightInfo.status}</span></p>
                    <p className="text-gray-600 mt-1">Arrives: {flightInfo.arrival?.airport} {flightInfo.arrival?.estimated ? `· est ${new Date(flightInfo.arrival.estimated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''} {flightInfo.arrival?.delay_min ? `· ${flightInfo.arrival.delay_min}min delay` : ''} {flightInfo.arrival?.gate ? `· Gate ${flightInfo.arrival.gate}` : ''}</p>
                  </>
                ) : (
                  <p className="text-gray-600">{flightInfo.note} <a href={flightInfo.link} target="_blank" rel="noreferrer" className="text-red-600 font-bold inline-flex items-center gap-1">Track on FlightAware <ExternalLink className="h-3 w-3" /></a></p>
                )}
              </div>
            )}
          </section>

          {/* Extras */}
          <section data-testid="extras-section">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1"><DollarSign className="h-3 w-3" /> Extra Charges (auto-added to total + client ledger)</p>
            {(b.extras || []).map(ex => (
              <div key={ex.extra_id} className="flex items-center justify-between border border-gray-100 px-2 py-1.5 mb-1 text-xs" data-testid={`extra-${ex.extra_id}`}>
                <span><b>{ex.label}</b>{ex.description ? ` · ${ex.description}` : ''}</span>
                <span className="flex items-center gap-2 font-mono font-bold">{f(ex.amount)}
                  <button onClick={() => removeExtra(ex.extra_id)} className="text-red-500" data-testid={`extra-del-${ex.extra_id}`}><Trash2 className="h-3 w-3" /></button>
                </span>
              </div>
            ))}
            <div className="grid grid-cols-4 gap-2 mt-2">
              <select value={extraForm.type} onChange={e => setExtraForm(p => ({ ...p, type: e.target.value }))} className="border rounded h-9 text-xs px-1" data-testid="extra-type">
                {EXTRA_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <Input type="number" placeholder="$" value={extraForm.amount} onChange={e => setExtraForm(p => ({ ...p, amount: e.target.value }))} className="h-9 text-xs" data-testid="extra-amount" />
              <Input placeholder="Note" value={extraForm.description} onChange={e => setExtraForm(p => ({ ...p, description: e.target.value }))} className="h-9 text-xs" />
              <Button size="sm" onClick={addExtra} disabled={!extraForm.amount} className="bg-red-600 hover:bg-red-700 h-9 text-xs" data-testid="extra-add">Add</Button>
            </div>
          </section>

          {/* Farm-out */}
          <section data-testid="farmout-section">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1"><ArrowRightLeft className="h-3 w-3" /> Farm-Out (affiliate covers this trip)</p>
            <label className="flex items-center gap-2 text-xs mb-2 cursor-pointer">
              <input type="checkbox" checked={farmout.is_farmout} onChange={e => setFarmout(p => ({ ...p, is_farmout: e.target.checked }))} data-testid="farmout-toggle" />
              This trip is farmed out to an affiliate
            </label>
            {farmout.is_farmout && (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Affiliate / Partner name" value={farmout.farmout_partner} onChange={e => setFarmout(p => ({ ...p, farmout_partner: e.target.value }))} className="h-9 text-xs" data-testid="farmout-partner" />
                <Input type="number" placeholder="Cost payable to affiliate $" value={farmout.farmout_cost} onChange={e => setFarmout(p => ({ ...p, farmout_cost: e.target.value }))} className="h-9 text-xs" data-testid="farmout-cost" />
                <Input placeholder="Their confirmation #" value={farmout.farmout_confirmation} onChange={e => setFarmout(p => ({ ...p, farmout_confirmation: e.target.value }))} className="h-9 text-xs" />
                <div className={`h-9 flex items-center justify-center text-xs font-bold border ${margin >= 0 ? 'text-green-700 border-green-200 bg-green-50' : 'text-red-600 border-red-200 bg-red-50'}`} data-testid="farmout-margin">
                  Margin: {f(margin)}
                </div>
              </div>
            )}
            <Button size="sm" onClick={saveFarmout} className="bg-black hover:bg-gray-800 h-8 text-xs mt-2" data-testid="farmout-save">Save Farm-Out</Button>
          </section>

          {/* Attachments */}
          <section>
            <AttachmentsSection entityType="booking" entityId={b.booking_id} title="Trip Attachments (contracts, receipts, itineraries)" uploadedBy="admin" />
          </section>
        </div>
      </Card>
    </div>
  );
}
